import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 7000-7999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const accountErrors = {
  // Live at 9 call sites across account.mongo.service.ts, account.helper.ts, and
  // passwordChange.service.ts. Standardized wording per this session's decision — several sites used
  // to carry cosmetic variants ("Account not found", "Account does not exist", "User not found.",
  // "User not found in this portal.") and 2 sent no real message at all; all now resolve through
  // this one entry. Two genuinely different concepts were split OUT of this code, see
  // ACCOUNT_EMAIL_MISSING_FOR_OTP and ACCOUNT_NOT_FOUND_SOMETHING_WENT_WRONG below.
  ACCOUNT_NOT_FOUND: {
    errorCode: 7001,
    errorMessage: t("Account not found"),
    isDynamic: false,
  },
  // The account was found, but has no email to send a password-change OTP to — distinct from
  // ACCOUNT_NOT_FOUND (7001), which means the account itself could not be located at all.
  // Live at packages/server/accounts/passwordChange/passwordChange.service.ts:230-234 — the account
  // was found, but has no email to send a password-change OTP to.
  // ⚠️ Renumbered 7038 -> 7039. Staging independently shipped 7038 as SIGNIN_METHOD_NOT_ALLOWED
  // (below), and staging is what is live; this code has only ever existed on this branch, so it is
  // the one that moves. A collision inside one file is exactly what the per-block split exists to
  // surface — the old single object hid three of these.
  ACCOUNT_EMAIL_MISSING_FOR_OTP: {
    errorCode: 7039,
    errorMessage: t("This account has no e-mail address to send the verification code to."),
    isDynamic: false,
  },
  // Live at accounts.controller.ts's checkIfAuthenticated (via middleware/helpers.ts's 2FA gate).
  // Both 2FA codes share this exact message today — they're differentiated by the `twoFactorAuthMode`
  // field ("qr"/"email"/"any"), not by message text. No frontend consumer currently reads either the
  // errorCode or twoFactorAuthMode for this endpoint (confirmed by a repo-wide grep).
  ACCOUNT_2FA_UNAUTHORIZED: {
    errorCode: 7004,
    errorMessage: t("Two-factor authentication is required to continue."),
    isDynamic: false,
  },
  ACCOUNT_2FA_NOT_FOUND: {
    errorCode: 7002,
    errorMessage: t("Two-factor authentication is required to continue."),
    isDynamic: false,
  },
  // Live at packages/server/accounts/account.mongo.service.ts's validateSignupOtp — no confirmed
  // frontend consumer.
  ACCOUNT_ALREADY_VERIFIED: {
    errorCode: 7005,
    errorMessage: t("Account already verified"),
    isDynamic: false,
  },
  // Live at packages/server/accounts/account.mongo.service.ts's initiateSignupEmailVerification — no
  // frontend consumer found in web or mobile.
  ACCOUNT_ALREADY_REGISTERED: {
    errorCode: 7006,
    errorMessage: t("Account already registered"),
    isDynamic: false,
  },
  // Social sign-up hit an email already registered via password/other means — distinct from
  // ACCOUNT_ALREADY_REGISTERED (7006), which is the same concept via the regular signup flow.
  // Frontend shows identical text for both (EnterEmail.tsx checks both codes).
  // Live at packages/server/accounts/socialAuth.service.ts:65. Displayed message stays "Something
  // went wrong" per explicit decision, even though the frontend (EnterEmail.tsx) actually shows its
  // own hardcoded "This email is already in use. Please proceed to sign in." for this code — this
  // mapper entry's text is not what's currently rendered, so don't assume it is without checking the
  // frontend call site.
  SOCIAL_ACCOUNT_ALREADY_REGISTERED: {
    errorCode: 7040,
    errorMessage: t("Social Account Already Registered"),
    isDynamic: false,
  },
  // Distinct from ACCOUNT_ALREADY_REGISTERED (was also 7006) so OTP flows and the
  // social-signup flow can't be confused by a handler that sees both.
  // Live at 5 sites: loginOtp.service.ts (2), passwordChange.service.ts (1) — all confirmed safe,
  // frontend already shows its own code-specific text and never read this errorMessage — plus
  // account.mongo.service.ts's verifyEmail stage (confirmed safe, VerifyEmail.tsx always shows its
  // own hardcoded text regardless) and validateSignupOtp (no confirmed consumer).
  // Standardized wording matches the shared OTP_WRONG_MESSAGE constant
  // (accounts/otpRateLimit.ts) — one outlier site ("Wrong OTP") used different wording for the same
  // concept.
  ACCOUNT_WRONG_OTP: {
    errorCode: 7034,
    errorMessage: t("The verification code is incorrect."),
    isDynamic: false,
  },
  // Live at 4 sites (loginOtp.service.ts x2, passwordChange.service.ts x1 — all confirmed safe; plus
  // account.mongo.service.ts's validateSignupOtp — no confirmed consumer). Standardized wording
  // matches the shared OTP_EXPIRED_MESSAGE constant; one outlier site ("OTP Expired") used different
  // wording for the same concept. The "no expiry recorded at all" case that used to share this code
  // was split out — see ACCOUNT_OTP_EXPIRY_MISSING.
  ACCOUNT_OTP_EXPIRED: {
    errorCode: 7007,
    errorMessage: t("Your verification code has expired. Please request a new one."),
    isDynamic: false,
  },
  // The OTP matched, but the account carries no expiry timestamp for it at all — a data-integrity
  // problem, not an actual timeout. Distinct from ACCOUNT_OTP_EXPIRED (7007), which means the code
  // genuinely aged out.
  // Live at packages/server/accounts/account.mongo.service.ts's validateSignupOtp — no confirmed
  // frontend consumer. Split out of ACCOUNT_OTP_EXPIRED —
  // a data-integrity case (no expiry recorded at all), not an actual timeout.
  ACCOUNT_OTP_EXPIRY_MISSING: {
    errorCode: 7041,
    errorMessage: t("This verification code has no expiry recorded. Please request a new one."),
    isDynamic: false,
  },
  // Live at packages/server/middleware/authorize.ts:168 (inside the 2FA gate — account passed 2FA
  // but couldn't resolve a portal). Never carried a real historical message (goes through the
  // UnauthorizedError passthrough, whose errorMessage was always undefined here) — text below is
  // written fresh, not sourced from prior usage. No confirmed frontend consumer either (the whole
  // /checkIfAuthenticated 2FA signal is unconsumed).
  ACCOUNT_PORTAL_IS_REQUIRED: {
    errorCode: 7008,
    errorMessage: t("A portal is required to continue."),
    isDynamic: false,
  },
  // Renumbered off 7012 (2026-09-10) — it collided with INSUFFICIENT_PERMISSION, which was already
  // live at 50+ files under that number. Only remaining use is spaces/space.service.ts's
  // "can't remove yourself from a Space" guard, which the file's own comment confirms is dead code
  // (unreachable on every live path).

  // Renumbered off a collision with INSUFFICIENT_PERMISSION this session (was 7012, now
  // errorCodes.ts's own value — see its comment there). Only remaining use is
  // spaces/space.service.ts:411-417, confirmed dead code (unreachable on every live path per the
  // file's own comment) — message preserved as-is in case it's ever reactivated.
  ACCOUNT_UNAUTHORIZED_ACTION: {
    errorCode: 7042,
    errorMessage: t("A user cannot remove themselves from a Space."),
    isDynamic: false,
  },
  // MASSIVE code — 354 call sites across the whole codebase, ~39 distinct message groups (see
  // backend-error-audit.md). This entry is NOT a migration of those 353 other sites — it exists only
  // because reviewflows.service.ts's addReviewFlowsToLibrary was fixed this session to send this
  // code for real (previously a bug: sent SOMETHING_WENT_WRONG with the wrong code's number stuffed
  // into errorMessage as a string). Text below matches the dominant existing convention
  // (`noPermissionMsg`, permissions/permissions.constant.ts) — do not assume this entry covers every
  // INSUFFICIENT_PERMISSION site; most still send their own inline errorMessage.
  INSUFFICIENT_PERMISSION: {
    errorCode: 7012,
    errorMessage: t("You do not have Required permission"),
    isDynamic: false,
  },
  // Live at 2 sites: account.mongo.service.ts's authenticate() (reason field carries this text,
  // unchanged) and passwordChange.service.ts's checkPasswordChangeIdentity (confirmed safe, no
  // reader). Sign-in-path site kept on the plain code deliberately (per explicit decision) — no
  // enumeration concern here, unlike ACCOUNT_NOT_FOUND, since a caller must already have a valid
  // email to reach this check.
  INCORRECT_PASSWORD: {
    errorCode: 7009,
    errorMessage: t("Password is incorrect"),
    isDynamic: false,
  },
  // Split by sign-in-path vs elsewhere this session — see INVITATION_EXPIRED_SOMETHING_WENT_WRONG.
  // This plain code is now used only at account.mongo.service.ts's forgotPassword (`message` field,
  // unchanged real text — anti-enumeration-safe by that endpoint's own existing design, since an
  // unknown email already gets the success envelope before reaching this branch).
  INVITATION_EXPIRED: {
    errorCode: 7010,
    errorMessage: t("Your invitation has expired, please contact the Portal Admin to resend the invitation."),
    isDynamic: false,
  },
  // Split by sign-in-path vs elsewhere this session — see ACCOUNT_UNVERIFIED_SOMETHING_WENT_WRONG.
  // This plain code is now used only at: account.mongo.service.ts's forgotPassword (dynamic — see
  // isDynamic below) and socialAuth.service.ts's 2 sites (errorMessage there is the literal generic
  // "Something went wrong" filler, deliberately left untouched).
  // The forgotPassword site's message interpolates SMTP_EMAIL_FROM (a server env var, not knowable
  // to the frontend), so it must travel as a param rather than a pre-built string.
  ACCOUNT_UNVERIFIED: {
    errorCode: 7011,
    errorMessage: t("Please accept the invitation sent on your email from [{{smtpEmailFrom}}]."),
    isDynamic: true,
    dynamicParams: ["smtpEmailFrom"],
  },
  // Sign-in path only, same reasoning as ACCOUNT_NOT_FOUND_SOMETHING_WENT_WRONG: the account is
  // unverified, but the client is deliberately told nothing more specific than "Something went
  // wrong" — a dedicated code keeps it traceable server-side without revealing account state to a
  // caller probing the sign-in endpoint. Distinct from ACCOUNT_UNVERIFIED (7011), which is used
  // where disclosing this IS acceptable (e.g. the forgot-password flow, already anti-enumerated by
  // its own design).
  // Sign-in path (authenticate()) only — see errorCodes.ts's comment on this code. `reason` carries
  // this exact text unchanged; only the code is dedicated, so the client can't infer which
  // invite-state applies from the errorCode alone.
  ACCOUNT_UNVERIFIED_SOMETHING_WENT_WRONG: {
    errorCode: 7043,
    errorMessage: t("Please Verify your account first"),
    isDynamic: false,
  },
  // Sign-in path only, same reasoning — distinct from INVITATION_EXPIRED (7010).
  // Sign-in path (authenticate()) only — see errorCodes.ts's comment on this code. `reason` carries
  // the real invitation-expired text unchanged; only the code is dedicated.
  INVITATION_EXPIRED_SOMETHING_WENT_WRONG: {
    errorCode: 7044,
    errorMessage: t("Your invitation has expired, please contact the Portal Admin to resend the invitation."),
    isDynamic: false,
  },
  // Live at 2 sites in filesystem-server.service.ts's saveFileToSpace. Confirmed safe — the only
  // frontend consumer (MailDetails.tsx) branches on errorCode and shows its own fixed
  // "Failed to save in Space." text regardless, never reads errorMessage.
  FAILED_TO_SAVE_AT_SPACE: {
    errorCode: 7013,
    errorMessage: t("Failed to save the files to the Space."),
    isDynamic: false,
  },
  // Live at 2 sites: spaces/space.service.ts (confirmed safe — listSpaces.tsx/WorkspaceList/index.tsx
  // both branch on this exact code and show their own hardcoded "Space not found." text) and
  // projectTemplates.service.ts (its own `message` field carries this text, unchanged — see that
  // file's own comment on the only consumer, SpacesTab.tsx).
  SPACE_NOT_FOUND: {
    errorCode: 7015,
    errorMessage: t("Space not found."),
    isDynamic: false,
  },
  // Live at 2 sites: spaces/space.service.ts (confirmed safe — frontend uses its own locally-held
  // spaceName, never reads the server's message here) and projectTemplates.service.ts (dynamic —
  // interpolates the space name; its `message` field still sends the pre-built string today, same
  // "documented for later" status as ACCOUNT_UNVERIFIED's dynamic entry).
  DELETE_DEFAULT_SPACE: {
    errorCode: 7016,
    errorMessage: t("Space '{{spaceName}}' cannot be deleted as this is a default space."),
    isDynamic: true,
    dynamicParams: ["spaceName"],
  },
  // Live at server.ts and helpers/multipartRoute.ts (identical text at both, cosmetic only). No
  // frontend consumer found — falls to whichever generic fallback the eventual reader has.
  INVALID_JSON: {
    errorCode: 7017,
    errorMessage: t("Invalid JSON"),
    isDynamic: false,
  },
  // Live at middleware/authorize.ts's 2FA gate — never carried a real errorMessage (goes through the
  // UnauthorizedError passthrough with none set). No confirmed frontend consumer either.
  ACCOUNT_SUSPENDED_IN_PORTAL: {
    errorCode: 7018,
    errorMessage: t("Your access to this portal has been suspended"),
    isDynamic: false,
  },
  // Live at account.mongo.service.ts's authenticate() — `reason` carries this text unchanged
  // (SignInTemplate reads it), errorMessage (an exact duplicate) was the only thing dropped.
  ACCOUNT_PASSWORD_EXPIRED: {
    errorCode: 7020,
    errorMessage: t("Your password has expired. Please reset your password."),
    isDynamic: false,
  },
  // Live at account.mongo.service.ts's resetPassword flow. Confirmed safe — SetPassword.tsx branches
  // on this exact code with its own translated text.
  ACCOUNT_RESET_TOKEN_INVALID: {
    errorCode: 7025,
    errorMessage: t("Either Token is invalid or link is expired"),
    isDynamic: false,
  },
  // Live at 2 real sites (account.mongo.service.ts's resetPassword, passwordChange.service.ts) —
  // both confirmed safe via SetPassword.tsx / ChangePassword.tsx code-specific handling. 3 further
  // sites in account.mongo.service.ts (~2978, ~2983, ~4842) carry the generic "Something went wrong"
  // filler and were deliberately left untouched: where a site's errorMessage is already the generic
  // filler, the code alone says no less than the text did, so there is nothing to gain by changing it.
  ACCOUNT_PASSWORD_TOO_WEAK: {
    errorCode: 7022,
    errorMessage: t("Your password isn't strong enough yet. Try using unrelated words or avoiding common patterns."),
    isDynamic: false,
  },
  // Live at passwordChange.service.ts (confirmed safe, ChangePassword.tsx). A second site in
  // account.mongo.service.ts (~6365) carries the generic "Something went wrong" filler and was left
  // untouched.
  ACCOUNT_PASSWORD_REUSED: {
    errorCode: 7021,
    errorMessage: t("The new password must be different from your recent passwords."),
    isDynamic: false,
  },
  // Live at passwordChange.service.ts's sendPasswordChangeOtp (confirmed safe, ChangePassword.tsx
  // reads errorCode + retryAfter, not errorMessage). 4 further sites in accounts.controller.ts carry
  // the generic "Something went wrong" filler and were deliberately left untouched.
  ACCOUNT_RATE_LIMIT_EXCEEDED: {
    errorCode: 7023,
    errorMessage: t("Too many verification codes requested. Please try again later."),
    isDynamic: false,
  },
  // Live at 2 real sites in socialAuth.service.ts (a Google access_token/nonce misuse case, and a
  // passthrough of an internal verification failure) — both dev/API-facing text, no confirmed
  // frontend consumer found. 11 further sites carry the generic "Something went wrong" filler and
  // were deliberately left untouched.
  // Broadened deliberately: this code has ELEVEN call sites — nonce mismatch on Google/Microsoft/
  // Apple, a missing credential, a missing `sub`, an already-linked provider — and the previous
  // message was written from just one of them, so it was wrong for the other ten. A general
  // sentence is also the right answer for a credential check: it leaks nothing.
  SOCIAL_SIGNUP_INVALID_CREDENTIAL: {
    errorCode: 7027,
    errorMessage: t("The sign-in credential could not be verified."),
    isDynamic: false,
  },
  // No current backend call site sends this — confirmed by grep, zero hits. Message is written from
  // the code's own name, not sourced from real usage.
  SOCIAL_SIGNUP_UNSUPPORTED_PROVIDER: {
    errorCode: 7026,
    errorMessage: t("This social provider is not supported"),
    isDynamic: false,
  },
  // The verified provider identity (sub) is already linked to a DIFFERENT account.
  SOCIAL_ACCOUNT_ALREADY_LINKED_ELSEWHERE: {
    errorCode: 7030,
    errorMessage: t("This social account is already linked to another account"),
    isDynamic: false,
  },
  SOCIAL_ACCOUNT_EMAIL_MISMATCH: {
    errorCode: 7173,
    errorMessage: t("The email on this {{provider}} account does not match your CUBE account email."),
    isDynamic: true,
    dynamicParams: ["provider"],
  },
  // Unlink refused: this is the user's only remaining sign-in method and no
  // password is set, so removing it would lock them out. Set a password first.
  SOCIAL_CANNOT_UNLINK_LAST_METHOD: {
    errorCode: 7031,
    errorMessage: t("Set a password before unlinking your only sign-in method"),
    isDynamic: false,
  },
  // Password-change OTP: too many wrong attempts — temporarily locked out.
  // Response carries `lockedUntil` (epoch ms) for the retry countdown.
  ACCOUNT_OTP_LOCKED: {
    errorCode: 7033,
    errorMessage: t("Too many incorrect attempts. Please try again later."),
    isDynamic: false,
  },
  // Password-change identity step: neither current password nor OTP supplied
  // for a user who has a password set.
  ACCOUNT_PASSWORD_OR_OTP_REQUIRED: {
    errorCode: 7032,
    errorMessage: t("Enter your current password or an emailed code to continue"),
    isDynamic: false,
  },
  ACCOUNT_NO_PASSWORD_SOCIAL_ONLY: {
    errorCode: 7028,
    errorMessage: t(
      "It looks like you signed up without setting a password. Please use the 'Send me a code' option to sign in.",
    ),
    isDynamic: false,
  },
  ACCOUNT_NO_PASSWORD_OTP_ONLY: {
    errorCode: 7035,
    errorMessage: t(
      "It looks like you signed up without setting a password. Please use the 'Send me a code' option to sign in.",
    ),
    isDynamic: false,
  },
  // The portal the invite was issued against no longer resolves, so there are no roles and no
  // frontendUrl to complete it with. Distinct from ACCOUNT_PORTAL_IS_REQUIRED (7008), which is a
  // missing REQUEST field rather than a missing portal document.
  ACCOUNT_INVITE_PORTAL_NOT_FOUND: {
    errorCode: 7036,
    errorMessage: t("Portal not found"),
    isDynamic: false,
  },
  // The invited account is already active, i.e. the invitation link has already been redeemed.
  // Distinct from INVITATION_EXPIRED (7010), which is a link that timed out without being used.
  ACCOUNT_INVITATION_ALREADY_USED: {
    errorCode: 7037,
    errorMessage: t("Invitation link already used"),
    isDynamic: false,
  },
  ACCOUNT_LOCKED: {
    errorCode: 7024,
    errorMessage: t("Account temporarily locked. Please try again in {{minutes}} minute(s)."),
    isDynamic: true,
    dynamicParams: ["minutes"],
  },
  ACCOUNT_SOCIAL_ACCOUNT_NOT_LINKED: {
    errorCode: 7029,
    errorMessage: t(
      "This {{provider}} account is not linked to your CUBE account. Please link it from Profile Settings first.",
    ),
    isDynamic: true,
    dynamicParams: ["provider"],
  },
  // Split off the generic RESOURCE_NOT_FOUND (9003): the portal resolved from `origin`/`portalId`
  // does not exist. Recurs across many signup-wizard stages and invite flows in
  // account.mongo.service.ts, always the same concept, so one code covers all of them.
  PORTAL_NOT_FOUND: {
    errorCode: 7046,
    errorMessage: t("Portal not found"),
    isDynamic: false,
  },
  // Split off RESOURCE_NOT_FOUND: the account has no linked in-house billing customer record yet
  // (submitTrialRequest / the OrderSummary signup stage). Distinct from ACCOUNT_NOT_FOUND (7001),
  // which means the account itself doesn't exist.
  BILLING_CUSTOMER_NOT_FOUND_FOR_ACCOUNT: {
    errorCode: 7047,
    errorMessage: t("Billing customer not found for this account"),
    isDynamic: false,
  },
  // Split off RESOURCE_NOT_FOUND: a billing quoteId recorded on the portal no longer resolves in the
  // billing system. Dynamic — the message interpolates the quoteId.
  BILLING_QUOTE_NOT_FOUND: {
    errorCode: 7048,
    errorMessage: t("Quote not found: {{quoteId}}"),
    isDynamic: true,
    dynamicParams: ["quoteId"],
  },
  // Split off RESOURCE_NOT_FOUND. Deliberately shared between two states — an invitation id that
  // matches no account, and one that matches an account with no passwordHash set — because this is
  // an unauthenticated, unvalidated-body route (checkIfPasswordSet); telling them apart would make
  // the code itself an invitation-id oracle. The message is specific about the outcome ("no
  // password set") while staying silent about which underlying case caused it.
  ACCOUNT_INVITE_NO_PASSWORD: {
    errorCode: 7049,
    errorMessage: t("No password has been set for this invitation."),
    isDynamic: false,
  },
  // Split off the generic SOMETHING_WENT_WRONG (9000) — account.mongo.service.ts's `submitTrialRequest`.
  // Covers both the "billing API gave a reason" and "billing API gave no reason" exits for the same
  // operation (creating the trial subscription in the billing system failed).
  BILLING_TRIAL_SUBSCRIPTION_FAILED: {
    errorCode: 7050,
    errorMessage: t("Billing Trail Subscription Failed"),
    isDynamic: false,
  },
  // 7051 held PARTNER_DIRECTORY_FETCH_FAILED, removed with the dead Zoho syncPartners path; free.
  // `cancelPayment`: no onlinePaymentId was supplied to cancel.
  CANCEL_PAYMENT_ID_REQUIRED: {
    errorCode: 7052,
    errorMessage: t("No payment was specified to cancel."),
    isDynamic: false,
  },
  // `cancelPayment`: the supplied onlinePaymentId is not a valid ObjectId.
  CANCEL_PAYMENT_ID_INVALID: {
    errorCode: 7053,
    errorMessage: t("The payment id is not valid."),
    isDynamic: false,
  },
  // `getCurrentStageOfSignUp`: `manage=true` was requested for a portal still on a trial plan —
  // subscription management only applies to paid subscriptions.
  TRIAL_SUBSCRIPTION_MANAGEMENT_BLOCKED: {
    errorCode: 7054,
    errorMessage: t("Subscription management is only available for paid subscriptions"),
    isDynamic: false,
  },
  // `validateUserIdUpdate`/`validatePermissionAndPortalCount`: the target account belongs to more
  // than one portal, so this profile-update path refuses rather than risk cross-portal corruption.
  ACCOUNT_MULTI_PORTAL_UPDATE_BLOCKED: {
    errorCode: 7055,
    errorMessage: t("Please contact the user to update the profile details."),
    isDynamic: false,
  },
  // `checkSeatsAvailability`: not enough seats remain (per-plan, per-product, or Pro-seat count) to
  // add the requested users. One code for all three seat-accounting branches — same concept, only the
  // seat model differs. Dynamic on the per-plan branch only (planName/totalSeats/neededSeats); the
  // other two branches send no params.
  INSUFFICIENT_SEATS_FOR_ASSIGNMENT: {
    errorCode: 7056,
    errorMessage: t(
      "This product cannot be assigned to more users because there aren't enough seats. Please add more seats from the subscription section first.",
    ),
    isDynamic: false,
  },
  // `inviteUsers` batch loop: the target account already has a pending (unaccepted) invite to this
  // portal. Distinct from ACCOUNT_ALREADY_REGISTERED (7006), which means the invite was already
  // accepted.
  // Unused — no call site sends this yet (the inviteUsers batch-exit split it was minted for was
  // reverted mid-session). Message written from the code's own name.
  ACCOUNT_ALREADY_INVITED: {
    errorCode: 7057,
    errorMessage: t("User is already invited"),
    isDynamic: false,
  },
  // `inviteUsers` batch loop: the request named an organisation id that does not resolve. Scoped to
  // the invite flow rather than reusing ORGANISATION_NOT_EXIST (5001), which belongs to the
  // org-structure/hierarchy module's own concept.
  // Unused — same caveat as ACCOUNT_ALREADY_INVITED above.
  ACCOUNT_INVITE_ORGANISATION_NOT_FOUND: {
    errorCode: 7058,
    errorMessage: t("Organisation not found"),
    isDynamic: false,
  },
  // `inviteUsers` batch loop: the request named a portal role id that does not resolve.
  // Unused — same caveat as ACCOUNT_ALREADY_INVITED above.
  ACCOUNT_INVITE_ROLE_NOT_FOUND: {
    errorCode: 7059,
    errorMessage: t("Role not found"),
    isDynamic: false,
  },
  // `defaultFunctionsOnPortalCreation`: `createPortal`'s insert was not acknowledged.
  PORTAL_CREATION_FAILED: {
    errorCode: 7060,
    errorMessage: t("Portal could not be created. Please try again."),
    isDynamic: false,
  },
  // register1's PortalCreation stage: the requested subdomain is already taken.
  PORTAL_SUBDOMAIN_UNAVAILABLE: {
    errorCode: 7061,
    errorMessage: t("This subdomain is not available. Try something else."),
    isDynamic: false,
  },
  // register1's OrgInfo stage: `insertOrganisation`'s upsert produced no id, or was not acknowledged.
  // Two call sites, same concept (one checks the returned id, the other the upsert's own status).
  ORGANISATION_CREATION_FAILED: {
    errorCode: 7062,
    errorMessage: t("The organisation could not be created."),
    isDynamic: false,
  },
  // register1's OrderSummary stage: `processQuoteData` returned no quote and no refusal of its own.
  BILLING_QUOTE_CREATION_FAILED: {
    errorCode: 7063,
    errorMessage: t("The quote for this portal could not be created."),
    isDynamic: false,
  },
  // register1's VerifyPaymentAmount/PaymentMethod stages: the portal has no `quote.quoteId` recorded
  // yet — the caller needs to complete the order-summary step first. Distinct from
  // BILLING_QUOTE_NOT_FOUND (7048), which means a quoteId IS recorded but doesn't resolve.
  BILLING_QUOTE_NOT_YET_CREATED: {
    errorCode: 7064,
    errorMessage: t("No quote found for this portal. Complete the order summary first."),
    isDynamic: false,
  },
  // register1's VerifyPaymentAmount stage: `updateQuote` (applying tax) failed.
  BILLING_QUOTE_TAX_UPDATE_FAILED: {
    errorCode: 7065,
    errorMessage: t("Failed to apply taxes to the quote"),
    isDynamic: false,
  },
  // `get2FAQrCode`: speakeasy generated a secret with no `otpauth_url`, so no QR code can be built.
  TWO_FACTOR_QR_GENERATION_FAILED: {
    errorCode: 7067,
    errorMessage: t("Could not generate the QR code"),
    isDynamic: false,
  },
  // accounts.controller.ts `authenticate2FAHandler`: the account has 2FA neither enabled nor
  // portal-mandated-with-a-mode, so there is nothing to verify against yet. 3 call sites, same
  // concept (user/portal has no 2FA method configured for this session to check).
  TWO_FACTOR_SETUP_REQUIRED: {
    errorCode: 7068,
    errorMessage: t("Please setup the two factor authentication first"),
    isDynamic: false,
  },
  // `sendEmailOtpHandler`: `sendEmail` reported the send failed (or was rejected) for the sign-in OTP.
  ACCOUNT_OTP_EMAIL_SEND_FAILED: {
    errorCode: 7070,
    errorMessage: t("Could not send the verification e-mail. Please try again."),
    isDynamic: false,
  },
  // 7071 (AI_ASSISTANT_UNAVAILABLE) is retired: nothing sends it. Do not reuse the number.
  // `sendLoginOtpHandler`/`authenticateLoginOtpHandler`: the portal has passwordless sign-in disabled
  // (`metaData.ALLOW_PASSWORDLESS_LOGIN === false`). Both call sites send the same message constant.
  PASSWORDLESS_LOGIN_DISABLED: {
    errorCode: 7072,
    errorMessage: t("Passwordless sign-in is not enabled for this portal."),
    isDynamic: false,
  },
  // `get2FAQrCodeHandler`/`handleTwoFactorAuthUpdate`: 2FA is managed by the portal's SSO identity
  // provider, so neither the QR code nor the settings can be touched here. Distinct from
  // SSO_PASSWORD_MANAGED (below), which is the same idea for password changes.
  TWO_FACTOR_SSO_MANAGED: {
    errorCode: 7073,
    errorMessage: t("Two-factor authentication is managed by your identity provider."),
    isDynamic: false,
  },
  // `handleTwoFactorAuthUpdate`: disabling 2FA (or changing its mode) requires completing a 2FA
  // check first (`TwoFactorAuthDone` not set in Redis for this session).
  TWO_FACTOR_COMPLETION_REQUIRED: {
    errorCode: 7074,
    errorMessage: t("Please complete two-factor authentication before modifying settings"),
    isDynamic: false,
  },
  // `getSleekTokenHandler`: multi-portal mode is off, or `SLEEK_SSO_KEY` is not configured.
  SLEEK_TOKEN_UNAVAILABLE: {
    errorCode: 7076,
    errorMessage: t("Something went wrong"),
    isDynamic: false,
  },
  // `sleekWebhookHandler`: the inbound Sleekplan webhook's `key` query param didn't match
  // `SLEEK_WEBHOOK_SECRET`. Answered to an external, non-CUBE caller — errorMessage stays on this
  // site regardless of the shared mapper, since Sleekplan's own infra reads it, not our frontend.
  WEBHOOK_KEY_INVALID: {
    errorCode: 7077,
    errorMessage: t("Invalid webhook key"),
    isDynamic: false,
  },
  // The ACTING user's own `accounts` row could not be resolved mid-request (`getUserDetailsObj(
  // sessionUserId)` came back empty) — their account was deleted while the session was still live, so
  // there is no creator/updater stub to stamp onto the write. Distinct from ACCOUNT_NOT_FOUND (7001),
  // which is a LOOKED-UP account (sign-in, invites, member add); this is the caller themselves, and it
  // keeps its own "User not found" copy.
  SESSION_USER_NOT_FOUND: {
    errorCode: 7079,
    errorMessage: t("User not found"),
    isDynamic: false,
  },
  // Same underlying condition as SESSION_USER_NOT_FOUND (7079) — the acting user's own `accounts` row
  // did not resolve — but a different sentence, and user-visible copy is not changed by this
  // migration, so the two wordings get their own codes. 3 call sites in the projects module; one of
  // them also fires when the row exists but has no `fullName`.
  SESSION_USER_COULD_NOT_BE_RESOLVED: {
    errorCode: 7080,
    errorMessage: t("Session user could not be resolved."),
    isDynamic: false,
  },
  // 7081-7086 is the permissions module's SOMETHING_WENT_WRONG (9000) split, continuing the 7xxx
  // Accounts/Permissions series. Five `noPermissionMsg` sites moved to INSUFFICIENT_PERMISSION (7012)
  // and one reused TEMPLATE_NOT_FOUND verbatim. Six sites stay generic (they literally say
  // "Something went wrong") and one interpolates a module name, so it keeps the standing
  // dynamic-message deferral.
  // 7081-7086 — the permissions module's split. Messages copied verbatim from the call sites.
  PERMISSION_USER_NOT_CHANGEABLE: {
    errorCode: 7081,
    errorMessage: t("Permission for this user cannot be changed"),
    isDynamic: false,
  },
  PERMISSION_MANAGED_BY_FORM_FLOW: {
    errorCode: 7082,
    errorMessage: t("This permission can only be updated through the corresponding form flow."),
    isDynamic: false,
  },
  PERMISSION_ONLY_UPDATE_PROMOTABLE_TO_FULL_ACCESS: {
    errorCode: 7083,
    errorMessage: t("Only can update permission users can be promoted to Full Access."),
    isDynamic: false,
  },
  // Distinct from INSUFFICIENT_PERMISSION (7012): this is specifically the right to change ANOTHER
  // user's access, and it ships its own sentence.
  PERMISSION_CHANGE_USER_ACCESS_DENIED: {
    errorCode: 7084,
    errorMessage: t("You Do Not have permission to change user access"),
    isDynamic: false,
  },
  // One of its two sites forwards a per-user reason, so that site keeps its message and takes only
  // the code.
  PERMISSION_UPDATE_FAILED: {
    errorCode: 7085,
    errorMessage: t("Permission not updated due to some error"),
    isDynamic: false,
  },
  PERMISSION_TEMPLATE_NAME_EXISTS: {
    errorCode: 7086,
    errorMessage: t("A template with same name already exists please enter a different name"),
    isDynamic: false,
  },
  // Shared, not schedule-scoped: 4 schedule sites ship this exact sentence and other modules use it
  // too. A THIRD wording of the same idea already exists as SESSION_USER_NOT_FOUND (7079,
  // "User not found") and SESSION_USER_COULD_NOT_BE_RESOLVED (7080, "Session user could not be
  // resolved.") — all three are harmonisation candidates if a copy change is ever approved.
  // 7087 — shared: four schedule sites plus other modules ship this exact sentence.
  CURRENT_USER_NOT_RESOLVED: {
    errorCode: 7087,
    errorMessage: t("Could not resolve the current user."),
    isDynamic: false,
  },
  // Shared: projectTemplates and organisations both ship this exact sentence.
  // The long tail of small modules — 7088/7089, 2006, 5002, 12001, 41xxx-50xxx.
  SESSION_USER_ACCOUNT_GONE: {
    errorCode: 7088,
    errorMessage: t("Session user account no longer exists"),
    isDynamic: false,
  },
  // 7090-7102 completes the accounts module's SOMETHING_WENT_WRONG split — the sites that were
  // left behind when the module was first processed. 7096-7102 are the Google social-auth path.
  // Completing the SOMETHING_WENT_WRONG (9000) / RESOURCE_NOT_FOUND (9003) split across the
  // modules that were processed earlier but still had specific-text sites on a generic code.
  ACCOUNT_DEACTIVATE_NOT_ALLOWED: {
    errorCode: 7090,
    errorMessage: t("You are not allowed deactivate this user"),
    isDynamic: false,
  },
  ACCOUNT_FETCH_USERS_INSUFFICIENT_DATA: {
    errorCode: 7091,
    errorMessage: t("Insufficient data to fetch users."),
    isDynamic: false,
  },
  ACCOUNT_MODULE_REQUIRED: {
    errorCode: 7092,
    errorMessage: t("Module is required"),
    isDynamic: false,
  },
  TWO_FACTOR_METHOD_UNVERIFIABLE: {
    errorCode: 7093,
    errorMessage: t("Two-factor authentication is configured with a method this server cannot verify."),
    isDynamic: false,
  },
  ACCOUNT_ID_OR_EMAIL_REQUIRED: {
    errorCode: 7094,
    errorMessage: t("id or email query parameter is required"),
    isDynamic: false,
  },
  ACCOUNT_PASSWORD_UPDATE_FAILED: {
    errorCode: 7096,
    errorMessage: t("Error while update."),
    isDynamic: false,
  },
  GOOGLE_CLIENT_ID_NOT_CONFIGURED: {
    errorCode: 7097,
    errorMessage: t("GOOGLE_CLIENT_ID is not configured"),
    isDynamic: false,
  },
  GOOGLE_ACCESS_TOKEN_UNVERIFIABLE: {
    errorCode: 7098,
    errorMessage: t("Could not verify Google access token"),
    isDynamic: false,
  },
  GOOGLE_TOKEN_WRONG_CLIENT: {
    errorCode: 7099,
    errorMessage: t("Google access token was not issued to this client"),
    isDynamic: false,
  },
  GOOGLE_PROFILE_FETCH_FAILED: {
    errorCode: 7100,
    errorMessage: t("Could not fetch Google profile"),
    isDynamic: false,
  },
  GOOGLE_TOKEN_SUBJECT_MISMATCH: {
    errorCode: 7101,
    errorMessage: t("Google token subject mismatch"),
    isDynamic: false,
  },
  GOOGLE_PROFILE_INCOMPLETE: {
    errorCode: 7102,
    errorMessage: t("Google profile is missing a subject or email"),
    isDynamic: false,
  },
  // 7103-7106 close out the INSUFFICIENT_PERMISSION (7012) split. 7012 itself stays — it is the
  // right code for the 291 sites that ship the shared `noPermissionMsg` ("You do not have Required
  // permission"). What needed splitting were the sites carrying a DIFFERENT sentence under it.
  //
  // The `errorMessages.noPermission` constant, which exists TWICE (estimates/estimate.constant.ts
  // and contracts/constants.ts) differing only in straight vs typographic apostrophe. 35 call sites.
  // A fold onto INSUFFICIENT_PERMISSION would retire this wording entirely — worth doing as a
  // deliberate copy decision, but it is not done here because it would change 35 sites' text.
  // The INSUFFICIENT_PERMISSION (7012) split — sites that carried a sentence OTHER than the
  // shared `noPermissionMsg`. Several were not permission failures at all.
  NO_PERMISSION_FOR_ACTION_LONG: {
    errorCode: 7103,
    errorMessage: t("You Don’t Have Required Permission to Perform this Action"),
    isDynamic: false,
  },
  ACTION_NOT_ALLOWED: {
    errorCode: 7104,
    errorMessage: t("Action not allowed"),
    isDynamic: false,
  },
  // 3 sites; one writes it with a trailing period (non-material).
  NO_PERMISSION_FOR_ACTION: {
    errorCode: 7105,
    errorMessage: t("You do not have permission to perform this action"),
    isDynamic: false,
  },
  FILE_EDIT_NO_PERMISSION: {
    errorCode: 7106,
    errorMessage: t("You do not have permission to edit this file"),
    isDynamic: false,
  },
  // 7107-7112 is the accounts module's NO_CHANGES_APPLIED (9005) split.
  // The NO_CHANGES_APPLIED (9005) split. 9005 stays for the one site whose sentence IS its
  // canonical message; everything below carried a sentence naming exactly what did not change.
  ACCOUNT_EMAIL_IN_USE_SIGN_IN: {
    errorCode: 7107,
    errorMessage: t("This email is already in use. Please proceed to sign in."),
    isDynamic: false,
  },
  ACCOUNT_EMAIL_ALREADY_REGISTERED: {
    errorCode: 7108,
    errorMessage: t("The email is already registered as a user on CUBE. Please use another email."),
    isDynamic: false,
  },
  ACCOUNT_PROFILE_UPDATE_FAILED: {
    errorCode: 7109,
    errorMessage: t("The profile could not be updated."),
    isDynamic: false,
  },
  PORTAL_PAYMENT_ALREADY_IN_PROGRESS: {
    errorCode: 7110,
    errorMessage: t("A payment is already in progress for this portal."),
    isDynamic: false,
  },
  PORTAL_NO_QUOTE_AWAITING_PAYMENT: {
    errorCode: 7111,
    errorMessage: t("No quote is awaiting payment on this portal."),
    isDynamic: false,
  },
  PORTAL_NO_PAID_SUBSCRIPTION: {
    errorCode: 7112,
    errorMessage: t("This portal has no paid subscription yet."),
    isDynamic: false,
  },
  INVITE_ALREADY_ACCEPTED: {
    errorCode: 7113,
    errorMessage: t("Invite already accepted"),
    isDynamic: false,
  },
  // 7114-7126 is the accounts module's VALIDATION_FAILED (9004) split.
  // The VALIDATION_FAILED (9004) split. Almost every site named the exact field or rule that
  // failed, so this is close to one code per site.
  ACCOUNT_EMAIL_IN_USE_NEW_REQUIRED: {
    errorCode: 7114,
    errorMessage: t("This email address is already in use. Please enter a new email address."),
    isDynamic: false,
  },
  ACCOUNT_USER_ID_IN_USE: {
    errorCode: 7115,
    errorMessage: t("This User ID is already in use. Please enter a new User ID."),
    isDynamic: false,
  },
  SIGNUP_STEP_UNKNOWN: {
    errorCode: 7116,
    errorMessage: t("This request does not name a signup step this server handles."),
    isDynamic: false,
  },
  ACCOUNT_EMAIL_REQUIRED: {
    errorCode: 7117,
    errorMessage: t("Email is required."),
    isDynamic: false,
  },
  PORTAL_NAME_AND_EMAIL_REQUIRED: {
    errorCode: 7118,
    errorMessage: t("Portal name and email are required."),
    isDynamic: false,
  },
  ORGANISATION_NAME_REQUIRED: {
    errorCode: 7119,
    errorMessage: t("Organisation name is required."),
    isDynamic: false,
  },
  BILLING_DETAILS_REQUIRED: {
    errorCode: 7120,
    errorMessage: t("Billing details are required."),
    isDynamic: false,
  },
  ACCOUNT_CUSTOMER_ID_INVALID: {
    errorCode: 7121,
    errorMessage: t("Invalid customer ID"),
    isDynamic: false,
  },
  DODO_CHECKOUT_CUSTOMER_EMAIL_REQUIRED: {
    errorCode: 7122,
    errorMessage: t("Customer email is required for DodoPayments checkout"),
    isDynamic: false,
  },
  INVITE_INVALID_OR_EXPIRED: {
    errorCode: 7124,
    errorMessage: t("Invalid or expired invite"),
    isDynamic: false,
  },
  ACCOUNT_OTP_INVALID: {
    errorCode: 7125,
    errorMessage: t("Invalid OTP"),
    isDynamic: false,
  },
  SESSION_INVALID_PLAIN: {
    errorCode: 7126,
    errorMessage: t("Invalid session"),
    isDynamic: false,
  },
  ADDONS_NO_ACCESS: {
    errorCode: 7019,
    errorMessage: t("This feature is not enabled for your portal."),
    isDynamic: false,
  },
  // `checkIfAuthenticated`: `req.cookies.authError` was set by an earlier failed sign-in attempt
  // (SSO/SAML callback failure etc.) — forwarded generically since the cookie names no specific cause.
  SIGNIN_FAILED: {
    errorCode: 7075,
    errorMessage: t("Sign-in failed. Please try again."),
    isDynamic: false,
  },
  // `rejectIfSsoEnabled`: password changes are refused when the portal's password is owned by the
  // SSO identity provider. The real user-facing text rides the separate `message` field
  // (SSO_PASSWORD_MANAGED_MESSAGE), not errorMessage — this code exists so the refusal itself is
  // traceable/branchable, distinct from TWO_FACTOR_SSO_MANAGED above (2FA, not password).
  SSO_PASSWORD_MANAGED: {
    errorCode: 7078,
    errorMessage: t("Your password is managed by your single sign-on provider."),
    isDynamic: false,
  },
  // Dynamic-message codes: their sentence carries a {{placeholder}} the CALLER fills via `params`.
  // Dynamic entries: `{{placeholder}}` templates the CLIENT interpolates via t(template, params).
  // `dynamicParams` documents which values the call site must send in `params`.
  ACCOUNT_RATE_LIMIT_RETRY_AFTER: {
    errorCode: 7127,
    errorMessage: t("Please try again in {{retryAfter}} seconds."),
    isDynamic: true,
    dynamicParams: ["retryAfter"],
  },
  FIELD_REQUIRED: {
    errorCode: 7128,
    errorMessage: t("{{name}} is required"),
    isDynamic: true,
    dynamicParams: ["name"],
  },
  ACCESS_TEMPLATE_CREATE_FAILED: {
    errorCode: 7136,
    errorMessage: t("The access template could not be created."),
    isDynamic: false,
  },
  ACCESS_TEMPLATE_DELETE_PARTIAL: {
    errorCode: 7137,
    errorMessage: t(
      "Some access templates were not deleted: a template that is in use, or the portal default, cannot be removed.",
    ),
    isDynamic: false,
  },
  ACCESS_TEMPLATE_UPDATE_FAILED: {
    errorCode: 7138,
    errorMessage: t("The access template was not updated."),
    isDynamic: false,
  },
  ACCESS_TEMPLATE_DUPLICATE_FAILED: {
    errorCode: 7139,
    errorMessage: t("The access template could not be duplicated."),
    isDynamic: false,
  },
  // Distinct from RECAPTCHA_UNUSUAL_TRAFFIC (9001): same concept, but the signup path's sentence
  // tells the user to contact support where 9001's tells them to try again. Two sentences, two codes.
  SIGNUP_RECAPTCHA_UNUSUAL_TRAFFIC: {
    errorCode: 7135,
    errorMessage: t("We are seeing some unusual traffic from your side. Please contact support"),
    isDynamic: false,
  },
  ACCOUNT_2FA_AUTHENTICATOR_SETUP_REQUIRED: {
    errorCode: 7130,
    errorMessage: t("Please setup the authenticator app first"),
    isDynamic: false,
  },
  ACCOUNT_NO_EMAIL_FOR_OTP: {
    errorCode: 7131,
    errorMessage: t("This account has no e-mail address to send the code to."),
    isDynamic: false,
  },
  ACCOUNT_2FA_LOCKED_BY_SSO: {
    errorCode: 7132,
    errorMessage: t(
      "Two-factor authentication settings cannot be changed when SSO is enabled. 2FA is managed by your identity provider.",
    ),
    isDynamic: false,
  },
  SIGNUP_ORGANISATION_CREATE_FAILED: {
    errorCode: 7133,
    errorMessage: t("Organisation could not be created."),
    isDynamic: false,
  },
  SIGNUP_BILLING_CUSTOMER_NOT_CREATED: {
    errorCode: 7134,
    errorMessage: t("Customer not created in billing system"),
    isDynamic: false,
  },
  // More {{placeholder}} templates (category C): backend sends values in `params`, client renders.
  ACCOUNT_SIGN_IN_RATE_LIMITED_RETRY_AFTER: {
    errorCode: 7140,
    errorMessage: t("Too many sign-in attempts. Please try again after {{retryAfter}} seconds."),
    isDynamic: true,
    dynamicParams: ["retryAfter"],
  },
  SOCIAL_PROVIDER_NOT_CONFIGURED: {
    errorCode: 7141,
    errorMessage: t("{{provider}} sign-in is not configured"),
    isDynamic: true,
    dynamicParams: ["provider"],
  },
  PERMISSION_MODULE_NOT_FOUND_OR_NO_ACCESS: {
    errorCode: 7129,
    errorMessage: t("{{moduleName}} does not exists or you do not have permission to view it"),
    isDynamic: true,
    dynamicParams: ["moduleName"],
  },
  // 7142-7152: the per-stage request guards on the signup funnel (`accounts.controller.ts`). Each
  // stage's handler `safeParse`s its own body and refuses with the list of fields that stage needs.
  // They all used to send the generic VALIDATION_FAILED (9004) beside a hand-written sentence, so
  // stripping the sentence would have collapsed eleven different "you are missing X" answers into one
  // "Please check the information you entered and try again." Each carries its own sentence instead.
  // The signup funnel's per-stage request guards (accounts.controller.ts). Each sentence is the one
  // that stage's handler already sent; they are separate codes so the client can still tell the user
  // WHICH fields that stage is missing, which one shared VALIDATION_FAILED could not.
  SIGNUP_VALID_EMAIL_REQUIRED: {
    errorCode: 7142,
    errorMessage: t("A valid email address is required."),
    isDynamic: false,
  },
  SIGNUP_EMAIL_AND_OTP_REQUIRED: {
    errorCode: 7143,
    errorMessage: t("An email address and a verification code are required."),
    isDynamic: false,
  },
  SIGNUP_EMAIL_AND_PASSWORD_REQUIRED: {
    errorCode: 7144,
    errorMessage: t("An email address and a password are required."),
    isDynamic: false,
  },
  SIGNUP_PORTAL_DETAILS_REQUIRED: {
    errorCode: 7145,
    errorMessage: t("A subdomain, a portal name and a valid email address are required."),
    isDynamic: false,
  },
  SIGNUP_USER_INFO_REQUIRED: {
    errorCode: 7146,
    errorMessage: t("An email address, a portal origin and a first name are required."),
    isDynamic: false,
  },
  SIGNUP_ORG_INFO_REQUIRED: {
    errorCode: 7147,
    errorMessage: t("An email address, a portal origin and an organisation name are required."),
    isDynamic: false,
  },
  SIGNUP_ORDER_SUMMARY_REQUIRED: {
    errorCode: 7148,
    errorMessage: t("An email address, a portal origin and an order summary are required."),
    isDynamic: false,
  },
  SIGNUP_QUOTE_TAX_DETAILS_REQUIRED: {
    errorCode: 7149,
    errorMessage: t("A portal origin is required, with a valid TDS percentage and TAN if one is given."),
    isDynamic: false,
  },
  SIGNUP_PORTAL_ORIGIN_REQUIRED: {
    errorCode: 7150,
    errorMessage: t("A portal origin is required."),
    isDynamic: false,
  },
  SIGNUP_EMAIL_AND_ORIGIN_REQUIRED: {
    errorCode: 7151,
    errorMessage: t("An email address and a portal origin are required."),
    isDynamic: false,
  },
  SIGNUP_PORTAL_REQUIRED_FOR_CHECK: {
    errorCode: 7152,
    errorMessage: t("This request must name the portal to check."),
    isDynamic: false,
  },
  // The same-tab social sign-in round trip rejects a `returnTo` that is not on ALLOWED_DOMAINS.
  ACCOUNT_RETURN_URL_INVALID: {
    errorCode: 7153,
    errorMessage: t("Invalid return URL"),
    isDynamic: false,
  },
  // 7154-7157: the 2FA QR guards and the signup success-page stage.
  ACCOUNT_2FA_OTP_REQUIRED_QR_MODE: {
    errorCode: 7154,
    errorMessage: t("OTP is required for QR mode"),
    isDynamic: false,
  },
  ACCOUNT_2FA_QR_NOT_GENERATED: {
    errorCode: 7155,
    errorMessage: t("Please generate a QR code first"),
    isDynamic: false,
  },
  SIGNUP_SUCCESS_PAGE_RECORDED_NO_COMPLETION: {
    errorCode: 7156,
    errorMessage: t("The success page was recorded; this signup stage reports no completion of its own."),
    isDynamic: false,
  },
  SIGNUP_NO_SUBSCRIPTION_TO_MARK_PAID: {
    errorCode: 7157,
    errorMessage: t("This portal has no subscription to mark as paid."),
    isDynamic: false,
  },
  // 7158: the invite flow's already-a-member refusal; the other three reasons already had codes (7057-7059).
  ACCOUNT_ALREADY_REGISTERED_IN_PORTAL: {
    errorCode: 7158,
    errorMessage: t("User already registered"),
    isDynamic: false,
  },
  // The portal's sign-in policy (`metaData.ALLOWED_SIGNIN_METHODS`) excludes the method used.
  // Answered by every sign-in endpoint and by the session check when a session minted by an excluded
  // method reaches a restricted portal. Web branches on this code in EnterEmail/EnterPassword.
  SIGNIN_METHOD_NOT_ALLOWED: {
    errorCode: 7038,
    errorMessage: t("This sign-in method is not enabled for this portal."),
    isDynamic: false,
  },
  // 7159-7172: the service-to-service auth middlewares. Both `verifyPortalCredentials` and
  // `verifyCloudSignature` used to refuse with prose alone (`next({ name: "UnauthorizedError",
  // errorMessage: "..." })`), which reached the client as a 401 carrying no machine-readable code.
  // The sentences below are the ones those middlewares already sent, moved here verbatim so the
  // refusal text is unchanged while the code becomes the thing on the wire. They are diagnostics for
  // a forwarder or a cloud caller, not end-user copy — no browser session reaches these gates.
  PORTAL_CREDENTIALS_MISSING: {
    errorCode: 7159,
    errorMessage: t("Missing portal credentials"),
    isDynamic: false,
  },
  PORTAL_CREDENTIALS_NOT_CONFIGURED: {
    errorCode: 7160,
    errorMessage: t("Portal credentials not configured"),
    isDynamic: false,
  },
  PORTAL_TOKEN_INVALID: {
    errorCode: 7161,
    errorMessage: t("Invalid portal token"),
    isDynamic: false,
  },
  PORTAL_TOKEN_MALFORMED: {
    errorCode: 7162,
    errorMessage: t("Malformed portal token"),
    isDynamic: false,
  },
  // The token decrypted, but the domain inside it is not the one the request claims.
  PORTAL_TOKEN_DOMAIN_MISMATCH: {
    errorCode: 7163,
    errorMessage: t("Portal token mismatch"),
    isDynamic: false,
  },
  // Also covers a non-numeric timestamp: both mean the token cannot be shown to be inside the
  // five-minute clock-skew window.
  PORTAL_TOKEN_EXPIRED: {
    errorCode: 7164,
    errorMessage: t("Portal token expired"),
    isDynamic: false,
  },
  PORTAL_TOKEN_BODY_MISMATCH: {
    errorCode: 7165,
    errorMessage: t("Portal token body mismatch"),
    isDynamic: false,
  },
  CLOUD_CREDENTIALS_MISSING: {
    errorCode: 7166,
    errorMessage: t("Missing cloud credentials"),
    isDynamic: false,
  },
  CLOUD_CREDENTIALS_NOT_CONFIGURED: {
    errorCode: 7167,
    errorMessage: t("Cloud credentials not configured"),
    isDynamic: false,
  },
  CLOUD_TOKEN_INVALID: {
    errorCode: 7168,
    errorMessage: t("Invalid cloud token"),
    isDynamic: false,
  },
  CLOUD_TOKEN_MALFORMED: {
    errorCode: 7169,
    errorMessage: t("Malformed cloud token"),
    isDynamic: false,
  },
  CLOUD_TOKEN_MISMATCH: {
    errorCode: 7170,
    errorMessage: t("Cloud token mismatch"),
    isDynamic: false,
  },
  CLOUD_TOKEN_EXPIRED: {
    errorCode: 7171,
    errorMessage: t("Cloud token expired"),
    isDynamic: false,
  },
  CLOUD_TOKEN_BODY_MISMATCH: {
    errorCode: 7172,
    errorMessage: t("Cloud token body mismatch"),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
